import {FC, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'

import {selectUser} from 'features/User'

import FeatureHighlight from './FeatureHighlight'
import {ACTIVE_FEATURE_HIGHLIGHT} from './features'
import {
  cleanupOrphanFeatureHighlightStorage,
  getFeatureHighlightStorageKey,
  isFeatureHighlightStorageAvailable,
} from './storage'
import {useFeatureHighlightEnabled} from './useFeatureHighlightEnabled'

/**
 * App-level host for the active feature highlight.
 * Point ACTIVE_FEATURE_HIGHLIGHT at a new meta object to switch features.
 */
const FeatureHighlightHost: FC = () => {
  const [storageAvailable] = useState(isFeatureHighlightStorageAvailable)
  const {emailId, primaryDomain} = useSelector(selectUser)
  const {enabled, isEligibilityPending} = useFeatureHighlightEnabled(
    ACTIVE_FEATURE_HIGHLIGHT,
  )
  const scopedStorageKey = getFeatureHighlightStorageKey(
    ACTIVE_FEATURE_HIGHLIGHT.storageKey,
    emailId,
    primaryDomain,
  )
  const feature = useMemo(
    () =>
      scopedStorageKey
        ? {
            ...ACTIVE_FEATURE_HIGHLIGHT,
            storageKey: scopedStorageKey,
          }
        : null,
    [scopedStorageKey],
  )

  useEffect(() => {
    if (!storageAvailable) return
    cleanupOrphanFeatureHighlightStorage()
  }, [storageAvailable])

  if (!storageAvailable || !feature) return null

  return (
    <FeatureHighlight
      key={feature.storageKey}
      feature={feature}
      enabled={enabled}
      isEligibilityPending={isEligibilityPending}
    />
  )
}

export default FeatureHighlightHost
